import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePlatform } from '../context/PlatformContext';
import { motion } from 'framer-motion';
import {
  FiUsers, FiFileText, FiSend, FiCheckCircle, FiTrendingUp, FiActivity,
  FiAward, FiShield, FiMail, FiAlertCircle, FiArrowRight, FiLayers,
  FiPlus, FiClock, FiArchive, FiBarChart2
} from 'react-icons/fi';

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } };
const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] } }
};

export default function Dashboard() {
  const {
    mode, candidates, applications, emailHistory, verificationLog,
    programs, batches, categories
  } = usePlatform();

  const stats = useMemo(() => ({
    total: candidates.length,
    generated: candidates.filter(c => c.docStatus === 'generated').length,
    sent: candidates.filter(c => c.emailStatus === 'sent').length,
    verified: verificationLog.length,
    failed: candidates.filter(c => c.emailStatus === 'failed').length,
    revoked: candidates.filter(c => c.certStatus === 'revoked').length,
    totalPrograms: (programs || []).length,
    activePrograms: (programs || []).filter(p => p.status === 'Active' && !p.isArchived).length,
    upcomingPrograms: (programs || []).filter(p => p.status === 'Upcoming' && !p.isArchived).length,
    completedPrograms: (programs || []).filter(p => p.status === 'Completed').length,
    totalBatches: (batches || []).length,
    totalCategories: (categories || []).length,
  }), [candidates, verificationLog, programs, batches, categories]);

  const kpiCards = [
    { title: 'Total Programs', value: stats.totalPrograms, icon: FiLayers, color: 'text-brand-600', iconBg: 'bg-brand-50', borderColor: 'border-brand-100' },
    { title: 'Active Programs', value: stats.activePrograms, icon: FiCheckCircle, color: 'text-emerald-600', iconBg: 'bg-emerald-50', borderColor: 'border-emerald-100' },
    { title: 'Total Candidates', value: stats.total, icon: FiUsers, color: 'text-violet-600', iconBg: 'bg-violet-50', borderColor: 'border-violet-100' },
    { title: 'Documents Generated', value: stats.generated, icon: FiFileText, color: 'text-sky-600', iconBg: 'bg-sky-50', borderColor: 'border-sky-100' },
    { title: 'Emails Dispatched', value: stats.sent, icon: FiSend, color: 'text-indigo-600', iconBg: 'bg-indigo-50', borderColor: 'border-indigo-100' },
    { title: 'Verifications', value: stats.verified, icon: FiShield, color: 'text-teal-600', iconBg: 'bg-teal-50', borderColor: 'border-teal-100' },
  ];

  const recentActivity = useMemo(() => {
    return [
      ...emailHistory.slice(0, 3).map(e => ({
        type: 'email', icon: FiMail,
        iconColor: e.status === 'sent' ? 'text-brand-600 bg-brand-50' : 'text-red-500 bg-red-50',
        text: `Email ${e.status === 'sent' ? 'sent' : 'failed'} to ${e.candidateName}`,
        sub: e.subject, time: e.sentAt
      })),
      ...verificationLog.slice(0, 2).map(v => ({
        type: 'verify', icon: FiShield,
        iconColor: 'text-emerald-600 bg-emerald-50',
        text: 'Credential verified', sub: v.certId || v.verifiedId, time: v.at
      })),
      ...candidates.filter(c => c.docStatus === 'generated').slice(-2).map(c => ({
        type: 'gen', icon: FiAward,
        iconColor: 'text-violet-600 bg-violet-50',
        text: `Document generated for ${c.name}`, sub: c.generatedId, time: c.createdAt
      })),
    ].filter(a => a.time).sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 6);
  }, [emailHistory, verificationLog, candidates]);

  const activeProgs = useMemo(() =>
    (programs || []).filter(p => !p.isArchived).slice(0, 3)
  , [programs]);

  const successRate = stats.generated > 0 ? Math.round((stats.sent / stats.generated) * 100) : 0;

  return (
    <motion.div className="space-y-8" initial="hidden" animate="visible" variants={stagger}>
      {/* ── Welcome Header ──────────────────────────────────────── */}
      <motion.div variants={fadeUp} className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h1 className="page-title text-[28px]">
            Welcome back, <span className="sky-text-gradient">Innovation Hacks</span>
          </h1>
          <p className="page-subtitle mt-2">
            Manage your internship programs, candidates, and documents from one place.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/programs" className="btn-secondary text-xs">
            <FiLayers className="w-3.5 h-3.5" /> All Programs
          </Link>
          <Link to="/programs" className="btn-primary text-xs">
            <FiPlus className="w-3.5 h-3.5" /> Create Internship
          </Link>
        </div>
      </motion.div>

      {/* ── Alert Banners ───────────────────────────────────────── */}
      {stats.failed > 0 && (
        <motion.div variants={fadeUp} className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-2xl text-sm text-red-800">
          <FiAlertCircle className="text-red-500 w-5 h-5 flex-shrink-0" />
          <span><strong>{stats.failed} email(s) failed.</strong> Go to Email Center to retry.</span>
          <Link to="/email" className="ml-auto text-red-600 font-semibold text-xs hover:text-red-700 flex items-center gap-1">
            View <FiArrowRight className="w-3 h-3" />
          </Link>
        </motion.div>
      )}

      {/* ── KPI Stat Cards ──────────────────────────────────────── */}
      <motion.div variants={fadeUp} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpiCards.map((card, i) => (
          <motion.div
            key={card.title}
            variants={fadeUp}
            className="stat-card group cursor-default"
          >
            <div className={`w-10 h-10 rounded-xl ${card.iconBg} ${card.color} border ${card.borderColor} flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300`}>
              <card.icon className="w-5 h-5" />
            </div>
            <div className="text-2xl font-bold text-navy-900 tracking-tight">{card.value}</div>
            <div className="text-xs text-slate-900 font-bold mt-1">{card.title}</div>
          </motion.div>
        ))}
      </motion.div>

      {/* ── Main Grid: Pipeline + Quick Programs + Activity ────── */}
      <div className="grid grid-cols-12 gap-6">
        {/* Pipeline Progress */}
        <motion.div variants={fadeUp} className="col-span-12 lg:col-span-5">
          <div className="glass-card rounded-2xl p-7 h-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-base font-semibold text-navy-900 flex items-center gap-2.5">
                <div className="w-1.5 h-5 bg-brand-500 rounded-full" />
                Document Pipeline
              </h3>
              <span className="text-xs text-slate-900 font-bold">{successRate}% delivery</span>
            </div>

            {stats.total === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-center">
                <div className="w-12 h-12 rounded-2xl bg-surface-100 flex items-center justify-center text-slate-300 mb-3">
                  <FiActivity className="w-6 h-6" />
                </div>
                <p className="text-sm text-slate-700 font-medium font-medium">Upload candidates to see pipeline metrics.</p>
              </div>
            ) : (
              <div className="space-y-5">
                {[
                  { label: 'Imported', val: stats.total, total: stats.total, color: 'bg-slate-400' },
                  { label: 'Generated', val: stats.generated, total: stats.total, color: 'bg-brand-500' },
                  { label: 'Email Sent', val: stats.sent, total: stats.total, color: 'bg-brand-600' },
                  { label: 'Verified', val: stats.verified, total: Math.max(stats.total, 1), color: 'bg-emerald-500' },
                ].map(step => (
                  <div key={step.label}>
                    <div className="flex justify-between mb-1.5">
                      <span className="text-[13px] font-medium text-slate-600">{step.label}</span>
                      <span className="text-[13px] font-semibold text-navy-900">{step.val} <span className="text-slate-400 font-normal">/ {step.total}</span></span>
                    </div>
                    <div className="h-2 bg-surface-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${step.total > 0 ? (step.val / step.total) * 100 : 0}%` }}
                        transition={{ duration: 0.8, ease: 'easeOut', delay: 0.3 }}
                        className={`h-full rounded-full ${step.color}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* Quick Programs Overview */}
        <motion.div variants={fadeUp} className="col-span-12 lg:col-span-4">
          <div className="glass-card rounded-2xl p-7 h-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-base font-semibold text-navy-900 flex items-center gap-2.5">
                <div className="w-1.5 h-5 bg-violet-500 rounded-full" />
                Recent Programs
              </h3>
              <Link to="/programs" className="text-xs text-brand-600 font-medium hover:text-brand-700 flex items-center gap-1 transition-colors">
                View all <FiArrowRight className="w-3 h-3" />
              </Link>
            </div>

            {activeProgs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-center">
                <div className="w-12 h-12 rounded-2xl bg-surface-100 flex items-center justify-center text-slate-300 mb-3">
                  <FiLayers className="w-6 h-6" />
                </div>
                <p className="text-sm text-slate-700 font-medium font-medium">No programs yet.</p>
                <Link to="/programs" className="mt-3 btn-primary text-xs py-2">
                  <FiPlus className="w-3 h-3" /> Create
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {activeProgs.map(p => {
                  const pCandidates = candidates.filter(c => c.programId === p.id);
                  return (
                    <Link
                      key={p.id}
                      to={`/programs/${p.id}`}
                      className="block p-3.5 rounded-xl bg-surface-50 border border-surface-200/60 hover:border-brand-200 hover:bg-brand-50/30 transition-all group"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-navy-900 truncate group-hover:text-brand-700 transition-colors">{p.name}</div>
                          <div className="text-xs text-slate-800 font-semibold mt-0.5">{p.month} {p.year} · {pCandidates.length} candidates</div>
                        </div>
                        <span className={`flex-shrink-0 badge ${
                          p.status === 'Active' ? 'badge-active' :
                          p.status === 'Upcoming' ? 'badge-upcoming' :
                          p.status === 'Completed' ? 'badge-completed' : 'badge-archived'
                        }`}>
                          {p.status}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>

        {/* Live Activity Feed */}
        <motion.div variants={fadeUp} className="col-span-12 lg:col-span-3">
          <div className="glass-card rounded-2xl p-7 h-full">
            <h3 className="text-base font-semibold text-navy-900 mb-6 flex items-center gap-2.5">
              <div className="w-1.5 h-5 bg-emerald-500 rounded-full" />
              Activity
            </h3>

            {recentActivity.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-center">
                <div className="w-10 h-10 rounded-xl bg-surface-100 flex items-center justify-center text-slate-300 mb-3">
                  <FiActivity className="w-5 h-5" />
                </div>
                <p className="text-sm text-slate-700 font-medium font-medium">No activity yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentActivity.map((act, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${act.iconColor}`}>
                      <act.icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[13px] text-slate-700 font-medium leading-snug">{act.text}</p>
                      {act.sub && <p className="text-[11px] text-slate-700 font-semibold font-mono mt-0.5 truncate">{act.sub}</p>}
                      <span className="text-[10px] text-slate-300 font-medium mt-1 block">
                        {act.time ? new Date(act.time).toLocaleString() : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* ── Quick Actions Bar ───────────────────────────────────── */}
      <motion.div variants={fadeUp} className="glass-card rounded-2xl p-6">
        <h3 className="text-base font-semibold text-navy-900 mb-4 flex items-center gap-2.5">
          <div className="w-1.5 h-5 bg-sky-500 rounded-full" />
          Quick Actions
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Create Program', icon: FiPlus, to: '/programs', color: 'text-brand-600 bg-brand-50 border-brand-100' },
            { label: 'Upload Candidates', icon: FiUsers, to: '/candidates', color: 'text-violet-600 bg-violet-50 border-violet-100' },
            { label: 'Generate Docs', icon: FiFileText, to: '/candidates', color: 'text-sky-600 bg-sky-50 border-sky-100' },
            { label: 'Send Emails', icon: FiMail, to: '/email', color: 'text-indigo-600 bg-indigo-50 border-indigo-100' },
            { label: 'View Analytics', icon: FiBarChart2, to: '/analytics', color: 'text-emerald-600 bg-emerald-50 border-emerald-100' },
            { label: 'Verify Credential', icon: FiShield, to: '/verification', color: 'text-teal-600 bg-teal-50 border-teal-100' },
          ].map(a => (
            <Link
              key={a.label}
              to={a.to}
              className={`flex items-center gap-3 p-3.5 rounded-xl border transition-all hover:shadow-sm group ${a.color}`}
            >
              <a.icon className="w-4 h-4 flex-shrink-0 group-hover:scale-110 transition-transform" />
              <span className="text-[13px] font-semibold">{a.label}</span>
            </Link>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}